import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import BadgePopup from '@/components/ai-learn/BadgePopup';
import { EarnedBadge } from '@/services/leaderboard.service';

interface ExamSuccessState {
  examTitle: string;
  totalQuestions: number;
  answeredCount: number;
  violationCount: number;
  submittedAt: string;
  pendingBadges?: EarnedBadge[];
}

export default function ExamSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [pendingBadges, setPendingBadges] = useState<EarnedBadge[]>([]);

  const state = location.state as ExamSuccessState | null;

  useEffect(() => {
    // If no state, redirect to certifications
    if (!state) {
      navigate('/learn/certifications', { replace: true });
      return;
    }

    // Show badge popup if there are pending badges
    if (state.pendingBadges && state.pendingBadges.length > 0) {
      setPendingBadges(state.pendingBadges);
      setShowBadgePopup(true);
    }
  }, [state, navigate]);

  if (!state) {
    return null;
  }

  return (
    <>
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Exam Submitted!</h2>
          {state.examTitle && (
            <p className="text-lg font-medium text-purple-600 mb-4">{state.examTitle}</p>
          )}
          <p className="text-gray-600 mb-6">
            Your exam has been successfully submitted. Thank you for completing the certification exam.
          </p>
          <div className="text-sm text-gray-500 space-y-1 mb-6">
            <p>Submitted at: {state.submittedAt}</p>
            <p>Total Questions: {state.totalQuestions}</p>
            <p>Answered: {state.answeredCount}</p>
            {state.violationCount > 0 && (
              <p className="text-amber-600 font-medium">Violations Recorded: {state.violationCount}</p>
            )}
          </div>
          <button
            onClick={() => navigate('/learn/certifications')}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Certifications
          </button>
        </div>
      </div>

      {/* Badge Popup */}
      {showBadgePopup && pendingBadges.length > 0 && (
        <BadgePopup
          badges={pendingBadges}
          onClose={() => {
            setShowBadgePopup(false);
            setPendingBadges([]);
          }}
        />
      )}
    </>
  );
}
